import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { Formik, Form } from 'formik';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, Col, Error, Row, NumberInput, TextInput, Legal } from '@figure/frontlib-gui';
import { httpActions } from '@figure/frontlib-onboarding-redux';
import { usePersonalLoan, usePersonalLoanRouteFsm, useRedirect } from 'shared/hooks';
import { Giact } from 'shared/constants';
import { isValidRoutingNumber } from 'shared/utils';
import { PageHeader } from 'shared/components/Typography';
import SectionFieldset from 'shared/components/Form/SectionFieldset';
import CheckContainer from 'shared/components/Check/CheckContainer';
import RoutingImage from 'shared/assets/images/account-routing.svg';
import CheckingImage from 'shared/assets/images/account-number.svg';

const ButtonContainer = styled.div`
  text-align: center;
`;

const ManualDisburseContainer = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { uuid: personalLoanUuid } = useParams();
  const { personalLoan, giactAccountVerification } = usePersonalLoan();
  const [isCheckingSelected, setIsCheckingSelected] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { t } = useTranslation();
  const customerServiceNumber = t('phone.customerService');
  const runRouter = usePersonalLoanRouteFsm();
  const { redirectToDashboard } = useRedirect();

  const giactAccountStatus = personalLoan.status?.giactAccountStatus ?? '';
  const linkedDisburseAccount =
    personalLoan.disburseAccount?.uuid &&
    personalLoan.disburseAccount?.routingNumber &&
    personalLoan.disburseAccount?.accountNumber;

  useEffect(() => {
    if (
      linkedDisburseAccount &&
      (giactAccountStatus === Giact.VERIFIED || giactAccountStatus === Giact.PENDING_REVIEW)
    ) {
      /**
       * In the case that disburseAccount did not fully save we will have apps stuck with no disburse but a giactAccountStatus
       * of PENDING_REVIEW or VERIFIED. In this case we will not trigger the httpAction.complete below and instead need to trigger here
       */
      dispatch(httpActions.complete());
      runRouter();
    }
  }, [giactAccountStatus, linkedDisburseAccount, runRouter, dispatch]);

  useEffect(() => {
    if (giactAccountStatus !== Giact.INITIAL) dispatch(httpActions.complete());
  }, [giactAccountStatus, dispatch]);

  const handleSubmit = async (data) => {
    dispatch(httpActions.request());
    giactAccountVerification(data);
    setIsSubmitted(true);
  };

  // setTimeout on a useEffect AFTER isSubmitted and giactAcctStatus updates
  useEffect(() => {
    let gTimer = null;
    if (isSubmitted === true) {
      gTimer = setTimeout(() => {
        if (giactAccountStatus === Giact.INITIAL) {
          dispatch(httpActions.complete());
          redirectToDashboard();
        }
      }, 20000);
    }
    return () => {
      gTimer && clearTimeout(gTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitted]);

  const handleBack = () => history.goBack();

  return (
    <>
      <PageHeader>Tell us where to send your funds.</PageHeader>
      <h3>
        Provide details for you preferred checking or savings account. The account must be in good
        standing, can’t be a business account, and must belong to you or someone listed on your
        application.
      </h3>
      <Formik
        enableReinitialize
        initialValues={{
          accountNumber: '',
          routingNumber: '',
          firstName: '',
          lastName: '',
          middleName: '',
          nameSuffix: '',
          plUuid: personalLoanUuid || '',
        }}
        validationSchema={yup.lazy(() =>
          yup.object().shape({
            routingNumber: yup
              .string()
              .required('Required')
              .test(
                'is-routing-number',
                'Invalid',
                (value) => value && isValidRoutingNumber(value)
              ),
            accountNumber: yup
              .string()
              .required('Required')
              .min(4, 'At least 4 digits')
              .max(17, 'Up to 17 digits')
              .test(
                'is-account-number',
                'Invalid',
                (value) => !value || /^[0-9]{4,17}$/.test(value)
              ),
            firstName: yup.string().trim().required('Required'),
            lastName: yup.string().trim().required('Required'),
            middleName: yup.string().trim(),
            nameSuffix: yup.string().trim(),
            plUuid: yup.string().required(),
          })
        )}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleSubmit, setFieldValue }) => (
          <Form onSubmit={handleSubmit}>
            <CheckContainer>
              <img
                src={isCheckingSelected ? CheckingImage : RoutingImage}
                alt={isCheckingSelected ? 'account number' : 'account routing number'}
              />
            </CheckContainer>

            <SectionFieldset>
              <Row>
                <Col>
                  <NumberInput
                    id="routing-number"
                    data-test-id="routing-number"
                    label="Routing number"
                    name="routing-number"
                    value={values.routingNumber}
                    isNumericString
                    format="#########"
                    errorText={touched.routingNumber && errors.routingNumber}
                    onChange={(v) => setFieldValue('routingNumber', v)}
                    onFocus={() => setIsCheckingSelected(false)}
                    required
                  />
                </Col>
                <Col>
                  <NumberInput
                    id="account-number"
                    data-test-id="account-number"
                    label="Account number"
                    name="account-number"
                    value={values.accountNumber}
                    isNumericString
                    format="##############"
                    errorText={touched.accountNumber && errors.accountNumber}
                    onChange={(v) => setFieldValue('accountNumber', v)}
                    onFocus={() => setIsCheckingSelected(true)}
                    required
                  />
                </Col>

                <Col>
                  <TextInput
                    id="firstName"
                    label="Account holder first name"
                    name="firstName"
                    data-test-id="firstName"
                    value={values.firstName}
                    maxLength="128"
                    errorText={touched.firstName && errors.firstName}
                    onChange={(e) => setFieldValue('firstName', e.target.value)}
                    required
                  />
                </Col>

                <Col>
                  <TextInput
                    id="lastName"
                    label="Account holder last name"
                    name="lastName"
                    data-test-id="lastName"
                    value={values.lastName}
                    maxLength="128"
                    errorText={touched.lastName && errors.lastName}
                    onChange={(e) => setFieldValue('lastName', e.target.value)}
                    required
                  />
                </Col>
                <Col>
                  <TextInput
                    id="middleName"
                    label="Account holder middle name"
                    name="middleName"
                    data-test-id="middleName"
                    value={values.middleName}
                    maxLength="128"
                    errorText={touched.middleName && errors.middleName}
                    onChange={(e) => setFieldValue('middleName', e.target.value)}
                  />
                </Col>

                <Col>
                  <TextInput
                    id="nameSuffix"
                    label="Account holder suffix"
                    name="nameSuffix"
                    data-test-id="nameSuffix"
                    value={values.nameSuffix}
                    maxLength="128"
                    errorText={touched.nameSuffix && errors.nameSuffix}
                    onChange={(e) => setFieldValue('nameSuffix', e.target.value)}
                  />
                </Col>
              </Row>
            </SectionFieldset>

            <Legal id="authorize-disclaimer">
              By pressing “Save and continue,” you authorize {t('companySingleName')} to disburse
              your funds to the account provided.
            </Legal>

            {giactAccountStatus === Giact.FAILED_VERIFICATION && (
              <Error display="block">
                Oops! Either we couldn't find an account using the information provided or your
                account is ineligible. If you have incorrectly entered your information, please try
                again. Otherwise, please use a different account. If you need help, contact us at{' '}
                {customerServiceNumber} and a customer support representative will be happy to
                assist you.
              </Error>
            )}

            <ButtonContainer>
              <Button large onClick={handleBack} type="button">
                Back
              </Button>
              <Button
                onClick={handleSubmit}
                primary
                large
                data-test-id="save-and-continue"
                type="button"
              >
                Save and continue
              </Button>
            </ButtonContainer>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default ManualDisburseContainer;
